import type React from "react";

import { Box, Typography } from "@mui/material";
import { type TooltipProps } from "recharts";
import { useTheme } from "styled-components";

interface TooltipRowProps {
  name: string;
  value: string;
  textColor: string;
}

const TooltipRow: React.FC<TooltipRowProps> = ({ name, value, textColor }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
    <Typography sx={{ fontSize: 14, color: textColor }}>
      {name}: {value}
    </Typography>
  </Box>
);

export interface ChartTooltipContentProps extends TooltipProps<number, string> {
  extraRows?: Array<{ dataKey: string; name: string; color?: string }>;
  labelFormatter?: (label: string) => string;
  valueFormatter?: (value: number) => string;
}

export const ChartTooltipContent: React.FC<ChartTooltipContentProps> = ({
  active,
  extraRows,
  label,
  payload,
  labelFormatter = l => l,
  valueFormatter = value => value.toString(),
}) => {
  const theme = useTheme();

  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${theme.colors.redesign.db20}`,
        borderRadius: 1,
        padding: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.redesign.db100, mb: 1 }}>
        {labelFormatter(label as string)}
      </Typography>
      {payload.map(entry => (
        <TooltipRow
          key={String(entry.dataKey)}
          name={String(entry.name)}
          value={valueFormatter(Number(entry.value))}
          textColor={theme.colors.redesign.db100}
        />
      ))}
      {extraRows?.map(row => (
        <TooltipRow
          key={row.dataKey}
          name={row.name}
          value={valueFormatter(Number(payload[0]?.payload?.[row.dataKey]))}
          textColor={theme.colors.redesign.db100}
        />
      ))}
    </Box>
  );
};
